import * as Sentry from '@sentry/react'
import { init as initConfig } from './initConfig'

import { GlobalAuthState, SupplementedAxiosError } from '@cibo/core'
import { EnvAuthCheck, IdleTimer, Loading, UserWebSocketProvider } from '@cibo/profile'
import { BuildInfo, CiboUiProvider, CloseButton } from '@cibo/ui'
import '@cibo/ui/src/theme/reset.scss'
import { CssBaseline } from '@mui/material'
import { QueryClientProvider } from '@tanstack/react-query'
import ErrorBoundary from 'components/ErrorBoundary'
import CONFIG from 'consts/envdex'
import 'normalize.css'
import { SnackbarKey, SnackbarProvider } from 'notistack'
import { Suspense, createRef, useState } from 'react'
import { createRoot } from 'react-dom/client'
import { Provider as ReduxProvider } from 'react-redux'
import { BrowserRouter, Route, Routes } from 'react-router-dom'
import { persistStore } from 'redux-persist'
import { PersistGate } from 'redux-persist/integration/react'
import { store } from 'store/configureStore'
import { Config } from 'types/Config'
import 'utils/initFirebase'
import { homepage } from '../package.json'
import { ImpactApp } from './ImpactApp'
import { PdfTemplates } from './PdfTemplates'
import { getQueryClient } from './store/getQueryClient'

//@ts-ignore
const isIntegrationTest = window.Cypress || window.localStorage.driver === 'playwright'
declare const __IMPACT_BUILD_INFO__: BuildInfo

initConfig()

if (
  navigator.userAgent !== 'Puppeteer' && // no Sentry while generating PDFs in mp-node-service
  !isIntegrationTest && // no Sentry during e2e tests
  (import.meta.env.PROD || import.meta.env.VITE_DEBUG_SENTRY) // prod builds or debug mode
) {
  Sentry.init({
    dsn: Config.get('SENTRY_DSN'),
    environment: Config.get('SENTRY_ENVIRONMENT'),
    ignoreErrors: ['ResizeObserver', 'Network Error', 'Non-Error promise rejection captured'],
    integrations: [Sentry.browserTracingIntegration()],

    // https://github.com/getsentry/sentry-javascript/issues/1964
    normalizeDepth: 5,

    // We recommend adjusting this value in production, or using tracesSampler
    // for finer control
    tracesSampleRate: 0.1,
    release: import.meta.env.VITE_RELEASE_NAME,

    beforeBreadcrumb: (breadcrumb, hint) => {
      switch (breadcrumb.category) {
        case 'ui.click': {
          if (hint) {
            const { tagName, textContent } = hint.event.target
            breadcrumb.message = `${tagName}: "${textContent}" | ${breadcrumb.message}`
          }
          break
        }
        default:
          break
      }
      return breadcrumb
    },

    beforeSend(event, hint) {
      // Check if it is an exception, and if so, try to set syslog context
      if (hint?.originalException instanceof Error) {
        const error = hint.originalException
        // @ts-ignore if isAxiosError is present then it's an axios error
        if (!!error?.isAxiosError) {
          Sentry.setContext('syslog', {
            context_id: (error as SupplementedAxiosError).context_id,
            context_path: (error as SupplementedAxiosError).context_path,
          })
        }
      }
      return event
    },
  })

  /**
   * CbSourceId is set by the backend and will span across all requests authed or not.
   */
  Sentry.withScope(scope => {
    scope.setTag('device_id', document.cookie.match(/CbSourceId=([^;]*)/)?.[1]?.split('@')[1])
  })
}

const queryClient = getQueryClient()

const Root = () => {
  const persistor = persistStore(store)
  const [buildInfo] = useState(__IMPACT_BUILD_INFO__)

  const notistackRef = createRef<SnackbarProvider>()
  const onClickDismiss = (key: SnackbarKey) => () => {
    notistackRef.current?.closeSnackbar(key)
  }

  return (
    <CiboUiProvider buildInfo={buildInfo}>
      <ErrorBoundary>
        <>{/* @ts-ignore */}</>
        <ReduxProvider store={store}>
          <UserWebSocketProvider baseUrl={Config.get('MP_BASE_URL_WS')} disable={isIntegrationTest}>
            <QueryClientProvider client={queryClient}>
              <EnvAuthCheck>
                <PersistGate persistor={persistor}>
                  <Suspense fallback={<Loading />}>
                    <BrowserRouter basename={`/${homepage}`}>
                      <SnackbarProvider
                        ref={notistackRef}
                        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
                        action={key => (
                          <CloseButton
                            sx={{ color: 'inherit' }}
                            onClick={onClickDismiss(key)}
                            ariaLabel="close"
                          />
                        )}
                      >
                        <CssBaseline />
                        <Routes>
                          <Route path="/pdf/*" element={<PdfTemplates />} />
                          <Route path="*" element={<ImpactApp />} />
                        </Routes>
                        <IdleTimer />
                      </SnackbarProvider>
                    </BrowserRouter>
                  </Suspense>
                </PersistGate>
              </EnvAuthCheck>
            </QueryClientProvider>
          </UserWebSocketProvider>
        </ReduxProvider>
      </ErrorBoundary>
    </CiboUiProvider>
  )
}

const container = document.getElementById('root')
// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
const root = createRoot(container!)
root.render(<Root />)

// expose state+config for Playwright
if (isIntegrationTest) {
  //@ts-ignore
  window.config = CONFIG
  //@ts-ignore
  window.queryClient = queryClient
}

// expose auth controller for Puppeteer (to generate PDF)
if (navigator.userAgent === 'Puppeteer' || isIntegrationTest) {
  //@ts-ignore
  window.globalAuthState = GlobalAuthState
  //@ts-ignore
  window.store = store
}

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
// serviceWorker.unregister()
